import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import CustomTable from "components/table/CustomTable";
import { useHistory } from "react-router-dom";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import Menu from "components/menu/MainMenu";
import { PLAN_LIST_API } from "constants/endpoints";
import { planColumns } from "./variables";
import { ENTITY_FILTER_TYPES } from "utils/common";
import { setFilterToLocalStorage } from "utils/common";
import { PLAN_STATUS } from "constants/filterDropdownData";
import { showToast } from "components/toast/Toast";
import { PLAN_STATUS_UPDATE } from "constants/endpoints";
import SharePlanComponent from "./modal/SharePlanComponent";
import MultiSelectMenu from "components/menu/MultiSelectMenu";
import { areAllStatusesSame } from "utils/common";
import { getFilterFromLocalStorage } from "utils/common";
import { SUB_STATUS } from "constants/filterDropdownData";
import { PLAN_USER_TYPE } from "constants/filterDropdownData";
import CreateSubscriptionModal from "./modal/CreateSubscriptionModal";
const PAGE_SIZE = 50;
const sortableColumns = ["name", "created_at", "updated_at"];

export default function Plans() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [planData, setPlanData] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [createSubscriptionModalOpen, setCreateSubscriptionModalOpen] =
    useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});
  const history = useHistory();
  const lsFilters = getFilterFromLocalStorage(ENTITY_FILTER_TYPES.PLAN_FILTER);

  const [filters, setFilters] = useState(
    lsFilters
      ? lsFilters
      : {
          status: "active",
        }
  );

  const [totalPages, setTotalPages] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortType: "created_at",
    sortDirection: "descending",
  });

  const today = new Date();
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(today.getDate() - 3);

  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] =
    useState(false);
  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };

  const fetchPlansData = useCallback(async () => {
    try {
      const queryString = new URLSearchParams(filters).toString();
      const res = await apiCall.get(
        `${PLAN_LIST_API}?${decodeURIComponent(queryString)}`,
        {}
      );
      if (res?.statusCode === HttpStatusCode.Ok) {
        const plans = res.data.plans || [];
        setPlanData(res?.data?.plans);
        setTotalPages(Math.ceil(plans.length / pageSize));
        setTotalCount(plans.length || 0);
      }
    } catch (error) {
      console.log("Error while fetching plans: ", error.message);
    }
  }, [currentPage, pageSize, sortConfig, filters]);

  const columns = useMemo(() => planColumns, []);
  const data = useMemo(() => planData, [planData]);

  const excludedColumnVisibility = ["_id"]; // Columns to exclude by default
  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = !excludedColumnVisibility.includes(
        column.accessor
      );
      return acc;
    }, {})
  );

  const handleRemoveAllFilter = () => {
    setFilters({
      status: "",
    });
  };

  const dropdownFilters = useMemo(() => {
    return [
      {
        name: "status",
        placeholder: "Status",
        value: filters?.status,
        data: PLAN_STATUS,
      },
      {
        name: "category",
        placeholder: "Category",
        value: filters?.category,
        data: SUB_STATUS,
      },
      {
        name: "user_type",
        placeholder: "User Type",
        value: filters?.user_type,
        data: PLAN_USER_TYPE,
      },
    ];
  }, [filters]);

  const allFiltersProps = {
    dropdowns: dropdownFilters,
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleUserAction = async ({
    updatedPropertyType,
    status,
    id: selectedId,
    selectedData,
  }) => {
    try {
      if (updatedPropertyType === "Create Subscription") {
        setSelectedPlan(selectedData);
        setCreateSubscriptionModalOpen(true);
        return;
      }

      if (updatedPropertyType === "Share") {
        setSelectedPlanId(selectedId);
        setIsModalOpen(true);
        return;
      }
      if (updatedPropertyType === "/admin/plans") {
        history.push(`/admin/plans?id=${selectedId}`);
        return;
      }
      if (updatedPropertyType === "Create Custom Plan") {
        history.push(`/admin/plans?id=${selectedId}&is_custom=1`);
        return;
      }

      await apiCall.patch(PLAN_STATUS_UPDATE, {
        plan_id: selectedId,
        status: status,
      });

      fetchPlansData();
      showToast({
        message: "Plan Status Updated Successfully",
        success: true,
        customStyle: {
          background: "#52c41a",
          color: "#fff",
          padding: "10px",
        },
      });
    } catch (error) {
      console.error("Error handling user action:", error);
      const errorMessage =
        error?.response?.data?.error?.message || error?.response?.data?.error;
      fetchPlansData();
      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  };

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find((item) => item._id === row._id);
      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item._id !== findData._id);
        } else {
          return [...prev, row];
        }
      });
    },
    [selectedRowsData]
  );

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor]
  );
  const selectedRowsSlug = selectedRowsData.map((item) => item.status);
  const selectedData = useMemo(
    () =>
      selectedRowsSlug.map((status) =>
        data.find((row) => row.status === status)
      ),
    [selectedRowsSlug, data]
  );
  const handleMultipleUpdateStatus = async ({ updatedStatus }) => {
    const idsToUpdate = selectedRowsData.map((item) => item._id);
    setSelectedPlanId(idsToUpdate);
    setIsModalOpen(true);
  };

  const handleApplyFilter = () => {
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.PLAN_FILTER, filters);
    setCurrentPage(1);

    setSelectedRowsData([]);
    fetchPlansData();
  };

  useEffect(() => {
    fetchPlansData();
  }, []);

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <>
      <Box height="100vh" bg="#F8FAFB">
        <Flex
          justify="space-between"
          p={{ base: 2, md: 4 }}
          align="center"
          direction={{ base: "column", md: "row" }}
          boxShadow="sm"
          borderRadius="sm"
          pos={{ base: "relative", md: "fixed" }}
          w={{ base: "100%", md: "calc(100vw - 96px)" }}
          zIndex={1000}
          gap={{ base: 3, md: 4 }}
        >
          <Flex
            gap={{ base: 2, md: 4 }}
            w={{ base: "full", md: "auto" }}
            direction={{ base: "column", md: "row" }}
            p={{ base: 1, md: 2 }}
          >
            <Flex
              gap={{ base: 2, md: 4 }}
              align="center"
              justify={{ base: "end", md: "space-between" }}
              w={{ base: "full", md: "auto" }}
              wrap="wrap"
            >
              <Button
                paddingX={6}
                leftIcon={<FiFilter />}
                rightIcon={<FiChevronsDown />} // Changed the icon to ChevronDown
                colorScheme="teal"
                variant="outline"
                borderRadius="md"
                size="sm"
                onClick={() => {
                  toggleSidebar();
                }}
              >
                Filter
              </Button>
              <ManageColumns
                columns={columns}
                handleColumnChange={handleColumnChange}
                columnVisibility={columnVisibility}
                setManageColumnSelectAllCheckbox={
                  setManageColumnSelectAllCheckbox
                }
                manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
              />
            </Flex>
          </Flex>
          <Flex
            gap={{ base: 4, md: 8 }}
            align="center"
            w={{ base: "full", md: "auto" }}
          >
            <Text
              borderRadius="5px"
              borderWidth="0.5px"
              borderColor="#637381"
              background="#F9F9FB"
              color="#637381"
              p={1}
              width={{ base: "full", md: "auto" }}
            >
              Total Plans: {totalCount}
            </Text>
            <Button
              borderRadius="5px"
              borderColor="#637381"
              background={"linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"}
              color="#ffffff"
              fontSize="sm"
              width={{ base: "full", md: "auto" }}
              colorScheme="teal"
              onClick={() => {
                history.push("/admin/plans");
              }}
            >
              Create Plans
            </Button>
          </Flex>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => {
              setCurrentPage(value);
            }}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            setPageSizeToLocalStorage={(value) =>
              setFilterToLocalStorage(
                ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE,
                value
              )
            }
          />
        </Flex>
        <SidebarFilter
          filters={allFiltersProps}
          handleFilterChange={handleFilterChange}
          handleApplyFilter={handleApplyFilter}
          isOpen={isOpenFilterSidebar}
          onClose={toggleSidebar}
          appliedFilters={filters}
          setFilters={setFilters}
          multiSelectKey={["user_type"]}
          enableMultiselect
          lsFilters={lsFilters ? lsFilters : { status: "" }}
          handleRemoveAllFilter={handleRemoveAllFilter}
        />
        {planData && planData.length !== 0 ? (
          <CustomTable
            columns={visibleColumns}
            data={data}
            selectedData={selectedData}
            onSelectedAction={setSelectedAction}
            selectedRowsData={selectedRowsData}
            handleRowSelect={handleRowSelect}
            setSelectedRowsData={setSelectedRowsData}
            sortableColumns={sortableColumns}
            sortConfig={sortConfig}
            setSortConfig={setSortConfig}
            height={{ base: "calc(100vh - 160px)", md: "calc(100vh - 80px)" }}
            menuComponent={
              <Menu
                data={Object.values(PLAN_STATUS)}
                onUpdatePropertyStatus={handleUserAction}
                selectedAction={selectedAction}
                dataType={"Plans"}
              />
            }
            multiSelectMenuComponent={
              selectedRowsSlug?.length > 0 &&
              areAllStatusesSame(selectedData.map((item) => item?.status)) && (
                <MultiSelectMenu
                  multiSelectCurrentValue={selectedData?.[0]?.status}
                  onMultipleUpdateStatus={handleMultipleUpdateStatus}
                  selectedAction={selectedAction}
                  selectedRowsData={selectedRowsData}
                  bgColor="#B2F5EA"
                  dataType={"Plans"}
                />
              )
            }
          />
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
      </Box>
      {isModalOpen && (
        <SharePlanComponent
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          planData={selectedAction}
        />
      )}
      {createSubscriptionModalOpen && (
        <CreateSubscriptionModal
          isOpen={createSubscriptionModalOpen}
          onClose={() => setCreateSubscriptionModalOpen(false)}
          planData={selectedPlan}
        />
      )}
    </>
  );
}
